import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUserRoles } from '../store/slices/authSlice';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import {
  computePriceStats,
  fetchG2gLatestPrices,
  fetchG2gPriceHistory,
  type G2gHistoryPoint,
  type G2gLatestPrice,
  type PriceStats,
} from '../services/g2gPriceService';
import { AdminDeleteSection } from './RMTPage/components/AdminDeleteSection';

// ── Constants ──
type TabValue = 'price' | 'helper';
type GameValue = 'PoE1' | 'PoE2';
type TimeRange = '1h' | '24h' | '3d' | '7d';

const CHART_HEIGHT = 350;

// ── Helpers ──
function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const WINDOW_HOURS: Record<TimeRange, number> = {
  '1h': 1,
  '24h': 24,
  '3d': 72,
  '7d': 168,
};

function filterWindow(data: G2gHistoryPoint[], hours: number): G2gHistoryPoint[] {
  if (!data || data.length === 0) return [];
  const now = new Date(data[data.length - 1].recordedAt).getTime();
  const cutoff = now - hours * 3600_000;
  return data.filter(d => new Date(d.recordedAt).getTime() >= cutoff && d.unitPriceUsd !== null);
}

interface ChangeCardSparklines {
  data24h: G2gHistoryPoint[];
  data3d: G2gHistoryPoint[];
  data7d: G2gHistoryPoint[];
}

// ── Component ──
const RMTPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabValue>('price');
  const [selectedGame, setSelectedGame] = useState<GameValue>('PoE1');
  const [latestPrices, setLatestPrices] = useState<G2gLatestPrice[] | null>(null);
  const [leagues, setLeagues] = useState<string[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<G2gHistoryPoint[] | null>(null);
  const [latestLoading, setLatestLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [fetchRevision, setFetchRevision] = useState(0);

  // Admin check
  const roles = useSelector(selectUserRoles);
  const isAdmin = roles.includes('admin');

  // Track fetch instances to discard stale completions.
  const latestFetchIdRef = useRef(0);
  const historyFetchIdRef = useRef(0);

  const loading = latestLoading || historyLoading;

  // ── Effect 1: Fetch latest prices (populates leagues dropdown) ──
  useEffect(() => {
    const fetchId = ++latestFetchIdRef.current;
    const controller = new AbortController();

    setLatestLoading(true);
    setError(null);
    setSelectedLeague(null);
    setHistoryData(null);

    const execute = async () => {
      try {
        const data = await fetchG2gLatestPrices(selectedGame, controller.signal);
        if (fetchId !== latestFetchIdRef.current) return;

        setLatestPrices(data);
        setLeagues([...new Set(data.map(p => p.league))]);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (fetchId !== latestFetchIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load price data');
      } finally {
        if (fetchId === latestFetchIdRef.current) {
          setLatestLoading(false);
        }
      }
    };

    execute();
    return () => {
      controller.abort();
    };
  }, [selectedGame, fetchRevision]);

  // ── Effect 2: Fetch history when a league is selected ──
  useEffect(() => {
    if (!selectedLeague) {
      setHistoryData(null);
      setHistoryLoading(false);
      return;
    }

    const fetchId = ++historyFetchIdRef.current;
    const controller = new AbortController();

    setHistoryData(null);
    setHistoryLoading(true);
    setError(null);

    const execute = async () => {
      try {
        const data = await fetchG2gPriceHistory(selectedGame, selectedLeague, 168, controller.signal);
        if (fetchId !== historyFetchIdRef.current) return;
        setHistoryData(data);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (fetchId !== historyFetchIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load history data');
      } finally {
        if (fetchId === historyFetchIdRef.current) {
          setHistoryLoading(false);
        }
      }
    };

    execute();
    return () => {
      controller.abort();
    };
  }, [selectedGame, selectedLeague, fetchRevision]);

  // ── Handlers ──
  const handleGameChange = (
    _: React.MouseEvent<HTMLElement>,
    newGame: GameValue | null,
  ) => {
    if (newGame !== null) {
      setSelectedGame(newGame);
    }
  };

  const handleLeagueChange = (_: React.SyntheticEvent, value: string | null) => {
    setSelectedLeague(value);
  };

  const handleRefresh = () => {
    setFetchRevision(v => v + 1);
  };

  const handleTimeRangeChange = (
    _: React.MouseEvent<HTMLElement>,
    newRange: TimeRange | null,
  ) => {
    if (newRange !== null) {
      setTimeRange(newRange);
    }
  };

  // ── Computed values ──
  const stats: PriceStats | null = useMemo(() => {
    if (!historyData || historyData.length === 0) return null;
    const valid = historyData.filter((d): d is G2gHistoryPoint & { unitPriceUsd: number } => d.unitPriceUsd !== null);
    if (valid.length === 0) return null;
    return computePriceStats(valid);
  }, [historyData]);

  const chartData = useMemo(() => {
    if (!historyData) return [];
    const hours = WINDOW_HOURS[timeRange];
    const filtered = filterWindow(historyData, hours);
    return filtered.filter(d => d.unitPriceUsd !== null);
  }, [historyData, timeRange]);

  const latestPriceInfo = useMemo(() => {
    if (!latestPrices || !selectedLeague) return null;
    return latestPrices.find(p => p.league === selectedLeague) ?? null;
  }, [latestPrices, selectedLeague]);

  // Sparkline data for change cards (always from full 168h window)
  const sparklines: ChangeCardSparklines = useMemo(() => {
    if (!historyData) return { data24h: [], data3d: [], data7d: [] };
    return {
      data24h: filterWindow(historyData, 24),
      data3d: filterWindow(historyData, 72),
      data7d: filterWindow(historyData, 168),
    };
  }, [historyData]);

  // ── Sub-render helpers ──
  const renderSparkline = (data: G2gHistoryPoint[], color: string) => {
    if (data.length === 0) return null;
    return (
      <Box sx={{ width: '100%', height: 40, mt: 1 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <AreaChart data={data}>
            <Area
              type='monotone'
              dataKey='unitPriceUsd'
              stroke={color}
              fill={color}
              fillOpacity={0.1}
              isAnimationActive={false}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderChangeCard = (
    title: string,
    change: number | null,
    sparklineData: G2gHistoryPoint[],
    sparklineColor: string,
  ) => {
    const isNegative = change !== null && change < 0;
    const isPositive = change !== null && change > 0;
    const Icon = isNegative ? TrendingDownIcon : isPositive ? TrendingUpIcon : TrendingFlatIcon;
    const iconColor = isNegative ? 'success.main' : isPositive ? 'error.main' : undefined;
    const valueColor = change !== null ? (isNegative ? 'success.main' : isPositive ? 'error.main' : 'text.secondary') : 'text.secondary';
    const formatted = change !== null ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}%` : '—';

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display='flex' alignItems='center' gap={1} mb={1}>
            {Icon && <Icon sx={iconColor ? { color: iconColor } : undefined} />}
            <Typography variant='body2' color='text.secondary'>{title}</Typography>
          </Box>
          <Typography variant='h5' fontWeight='bold' color={valueColor}>
            {formatted}
          </Typography>
          {renderSparkline(sparklineData, sparklineColor)}
        </CardContent>
      </Card>
    );
  };

  // ── Render ──
  return (
    <PageLayout>
      <PageHeader
        title='RMT Dashboard'
        subtitle='Divine Orb prices from G2G — latest rates and price history'
      />

      {/* ── Sub-tabs ── */}
      <Tabs
        value={activeTab}
        onChange={(_, v: TabValue) => setActiveTab(v)}
        sx={{ mb: 3 }}
      >
        <Tab value='price' label='Price' />
        <Tab value='helper' label='Helper' />
      </Tabs>

      {/* ══════ PRICE TAB ══════ */}
      {activeTab === 'price' && (
        <>
          {/* Controls row */}
          <Box display='flex' alignItems='center' gap={2} mb={2}>
            <ToggleButtonGroup
              value={selectedGame}
              exclusive
              onChange={handleGameChange}
              size='small'
            >
              <ToggleButton value='PoE1'>PoE1</ToggleButton>
              <ToggleButton value='PoE2'>PoE2</ToggleButton>
            </ToggleButtonGroup>

            <Autocomplete
              options={leagues}
              value={selectedLeague}
              onChange={handleLeagueChange}
              renderInput={(params) => (
                <TextField {...params} label='League' size='small' />
              )}
              sx={{ minWidth: 220 }}
              disabled={loading}
            />

            <IconButton onClick={handleRefresh} disabled={loading} aria-label='Refresh'>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* ── Error state ── */}
          {!latestLoading && error && (
            <Alert
              severity='error'
              action={
                <IconButton color='inherit' size='small' onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              }
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          )}

          {/* ── No league selected ── */}
          {!loading && !error && latestPrices !== null && leagues.length > 0 && !selectedLeague && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant='h6' color='text.secondary'>
                Select a league to view price statistics
              </Typography>
            </Box>
          )}

          {/* ── League with no data ── */}
          {!historyLoading && !error && selectedLeague && historyData !== null && historyData.length === 0 && (
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ textAlign: 'center', py: 4 }}
            >
              No price data yet for {selectedLeague}.
            </Typography>
          )}

          {/* ── History loading skeletons ── */}
          {historyLoading && selectedLeague && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={i}>
                  <Skeleton variant='rounded' height={160} />
                </Grid>
              ))}
            </Grid>
          )}

          {/* ── Data (stats cards + chart) ── */}
          {!historyLoading && !error && selectedLeague && historyData !== null && historyData.length > 0 && stats && (
            <>
              {/* Stats cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Card 1: Current Price */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display='flex' alignItems='center' gap={1} mb={1}>
                        <AttachMoneyIcon color='primary' />
                        <Typography variant='body2' color='text.secondary'>
                          Current Price
                        </Typography>
                      </Box>
                      <Typography variant='h4' fontWeight='bold'>
                        {stats.currentPrice !== null ? `$${stats.currentPrice.toFixed(4)}` : '—'}
                      </Typography>
                      {latestPriceInfo && (
                        <Typography variant='caption' color='text.secondary'>
                          {latestPriceInfo.sellerName}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Card 2: 24h Change */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                  {renderChangeCard('24h Change', stats.change24h, sparklines.data24h, '#8884d8')}
                </Grid>

                {/* Card 3: 3 Day Change */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                  {renderChangeCard('3 Day Change', stats.change3d, sparklines.data3d, '#82ca9d')}
                </Grid>

                {/* Card 4: 7 Day Change */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                  {renderChangeCard('7 Day Change', stats.change7d, sparklines.data7d, '#ffc658')}
                </Grid>

                {/* Card 5: 24h Range */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display='flex' alignItems='center' gap={1} mb={1}>
                        <SwapVertIcon color='primary' />
                        <Typography variant='body2' color='text.secondary'>
                          24h Range
                        </Typography>
                      </Box>
                      <Typography variant='h6' fontWeight='bold'>
                        {stats.min24h !== null && stats.max24h !== null
                          ? `$${stats.min24h.toFixed(4)} \u2013 $${stats.max24h.toFixed(4)}`
                          : '—'}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {stats.variance24h !== null
                          ? `\u00B1${stats.variance24h.toFixed(2)}% variance`
                          : '—'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Card 6: 1h Average */}
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display='flex' alignItems='center' gap={1} mb={1}>
                        <TimelineIcon color='primary' />
                        <Typography variant='body2' color='text.secondary'>
                          1h Average
                        </Typography>
                      </Box>
                      <Typography variant='h5' fontWeight='bold'>
                        {stats.avg1h !== null ? `$${stats.avg1h.toFixed(4)}` : '—'}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {stats.dataPoints1h} data points
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Chart section */}
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={timeRange}
                  exclusive
                  onChange={handleTimeRangeChange}
                  size='small'
                >
                  <ToggleButton value='1h'>1h</ToggleButton>
                  <ToggleButton value='24h'>24h</ToggleButton>
                  <ToggleButton value='3d'>3d</ToggleButton>
                  <ToggleButton value='7d'>7d</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <ContentSection>
                <EnhancedCard>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>
                      {chartData.length > 0 ? (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray='3 3' />
                            <XAxis
                              dataKey='recordedAt'
                              tick={{ fontSize: 12 }}
                              tickFormatter={formatTimestamp}
                              interval='preserveStartEnd'
                            />
                            <YAxis
                              tickFormatter={(v: number) => `$${v.toFixed(4)}`}
                            />
                            <RechartsTooltip
                              labelFormatter={(label: string) =>
                                new Date(label).toLocaleString()
                              }
                              formatter={(value: number) => [
                                `$${value.toFixed(4)}`,
                                'Unit Price (USD)',
                              ]}
                            />
                            <Line
                              type='monotone'
                              dataKey='unitPriceUsd'
                              stroke='#8884d8'
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              connectNulls={false}
                              name={selectedLeague}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ textAlign: 'center', py: 4 }}
                        >
                          No chart data available for the selected time range.
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </EnhancedCard>
              </ContentSection>

              {/* Admin Tools Section */}
              {isAdmin && (
                <AdminDeleteSection leagues={leagues} game={selectedGame} />
              )}
            </>
          )}
        </>
      )}

      {/* ══════ HELPER TAB ══════ */}
      {activeTab === 'helper' && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <HelpOutlineIcon
            sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
          />
          <Typography variant='h4' color='text.secondary' gutterBottom>
            Coming Soon
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            Helper tools and calculators for RMT operations will appear here.
          </Typography>
        </Box>
      )}
    </PageLayout>
  );
};

export default RMTPage;